if (!customElements.get('media-with-text-video')) {
  class MediaWithTextVideo extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.previewImage = null;
      this.playButton = null;
      this.pauseButton = null;
      this.isPlaying = false;
      this.sectionId = null;
      this.isHovered = false;
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      this.previewImage = this.querySelector('.media-with-text__preview-image');
      this.playButton = this.querySelector('.media-with-text__play-button');
      this.pauseButton = this.querySelector('.media-with-text__pause-button');
      this.sectionId = this.closest('.media-with-text')?.getAttribute('id');

      if (!this.video) return;

      if (this.previewImage) {
        this.previewImageClickHandler = () => {
          if (!this.isPlaying && this.playButton) {
            this.handlePlay();
          }
        };
        this.previewImage.addEventListener('click', this.previewImageClickHandler);
      }

      if (this.video) {
        this.videoClickHandler = (e) => {
          e.stopPropagation(); // Prevent wrapper click handler from firing
          if (this.isPlaying) {
            this.handlePause();
          }
          else if(!this.isPlaying) {
           this.handlePlay();
          }
        };
        this.video.addEventListener('click', this.videoClickHandler);
      }

      // Add hover handlers to show pause/play button on interaction (desktop)
      this.mouseEnterHandler = () => {
        this.isHovered = true;
        if (this.isPlaying && this.pauseButton) {
          this.showPauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.showPlayButton();
        }
      };

      this.mouseLeaveHandler = () => {
        this.isHovered = false;
        if (this.isPlaying && this.pauseButton) {
          this.hidePauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.hidePlayButton();
        }
      };

      // Add touch handlers for mobile
      this.touchStartHandler = () => {
        this.isHovered = true;
        if (this.isPlaying && this.pauseButton) {
          this.showPauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.showPlayButton();
        }
      };

      this.touchEndHandler = () => {
        this.isHovered = false;
        if (this.isPlaying && this.pauseButton) {
          this.hidePauseButton();
        } else if (!this.isPlaying && this.playButton && this.video && this.video.style.display !== 'none') {
          this.hidePlayButton();
        }
      };

      this.addEventListener('mouseenter', this.mouseEnterHandler);
      this.addEventListener('mouseleave', this.mouseLeaveHandler);
      this.addEventListener('touchstart', this.touchStartHandler);
      this.addEventListener('touchend', this.touchEndHandler);

      let muted = false;
      let loop = false;

      // Check custom element attributes first, then video element attributes
      if (this.hasAttribute('data-muted')) {
        muted = this.getAttribute('data-muted') === 'true';
      } else if (this.video && this.video.hasAttribute('data-muted')) {
        muted = this.video.getAttribute('data-muted') === 'true';
      }

      if (this.hasAttribute('data-loop')) {
        loop = this.getAttribute('data-loop') === 'true';
      } else if (this.video && this.video.hasAttribute('data-loop')) {
        loop = this.video.getAttribute('data-loop') === 'true';
      }

      if (this.video) {
        this.video.muted = muted;
        this.video.loop = loop;
        this.video.controls = false;
        this.video.playsInline = true;
        this.video.preload = 'metadata';
      }

      // Set up event listeners
      this.setupEventListeners();

      // Show preview image initially, hide video
      this.showPreview();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    setupEventListeners() {
      this.handlePlayBound = this.handlePlay.bind(this);
      this.handlePauseBound = this.handlePause.bind(this);
      this.onVideoPlayBound = this.onVideoPlay.bind(this);
      this.onVideoPauseBound = this.onVideoPause.bind(this);
      this.onVideoEndedBound = this.onVideoEnded.bind(this);

      if (this.playButton) {
        this.playButton.addEventListener('click', this.handlePlayBound);
      }

      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent wrapper click handler from firing
          this.handlePauseBound();
        });
      }

      if (this.video) {
        this.video.addEventListener('play', this.onVideoPlayBound);
        this.video.addEventListener('pause', this.onVideoPauseBound);
        this.video.addEventListener('ended', this.onVideoEndedBound);
      }
    }

    removeEventListeners() {
      if (this.playButton && this.handlePlayBound) {
        this.playButton.removeEventListener('click', this.handlePlayBound);
      }

      if (this.pauseButton && this.handlePauseBound) {
        this.pauseButton.removeEventListener('click', this.handlePauseBound);
      }

      if (this.previewImage && this.previewImageClickHandler) {
        this.previewImage.removeEventListener('click', this.previewImageClickHandler);
      }

      if (this.video && this.videoClickHandler) {
        this.video.removeEventListener('click', this.videoClickHandler);
      }

      if (this.mouseEnterHandler) {
        this.removeEventListener('mouseenter', this.mouseEnterHandler);
      }

      if (this.mouseLeaveHandler) {
        this.removeEventListener('mouseleave', this.mouseLeaveHandler);
      }

      if (this.touchStartHandler) {
        this.removeEventListener('touchstart', this.touchStartHandler);
      }

      if (this.touchEndHandler) {
        this.removeEventListener('touchend', this.touchEndHandler);
      }

      if (this.video) {
        if (this.onVideoPlayBound) {
          this.video.removeEventListener('play', this.onVideoPlayBound);
        }
        if (this.onVideoPauseBound) {
          this.video.removeEventListener('pause', this.onVideoPauseBound);
        }
        if (this.onVideoEndedBound) {
          this.video.removeEventListener('ended', this.onVideoEndedBound);
        }
      }
    }

    handlePlay() {
      // Pause all other videos in the section
      this.pauseOtherVideos();

      // Play this video
      if (this.video) {
        this.video.play().catch((error) => {
          console.error('Error playing video:', error);
        });
      }

      // Hide preview and play button, show video
      this.hidePreview();
    }

    handlePause() {
      if (this.video && !this.video.paused) {
        this.video.pause();
      }
    }

    pauseOtherVideos() {
      if (!this.sectionId) return;

      const section = document.getElementById(this.sectionId);
      if (!section) return;

      const allVideoElements = section.querySelectorAll('media-with-text-video');
      allVideoElements.forEach((element) => {
        if (element !== this && element.video && !element.video.paused) {
          element.video.pause();
        }
      });
    }

    onVideoPlay() {
      this.isPlaying = true;
      // Update button visibility - will respect hover state
      this.updateButtonVisibility();
    }

    onVideoPause() {
      this.isPlaying = false;
      this.updateButtonVisibility();
      // Hide pause button when paused
      this.hidePauseButton();
    }

    onVideoEnded() {
      this.isPlaying = false;
      this.showPreview();
    }

    showPreview() {
      if (this.previewImage) {
        this.previewImage.style.display = 'block';
      }
      if (this.playButton) {
        this.playButton.style.display = 'flex';
        this.playButton.style.opacity = '0.5';
      }
      if (this.pauseButton) {
        this.pauseButton.style.display = 'none';
      }
      if (this.video) {
        this.video.style.display = 'none';
      }
    }

    hidePreview() {
      if (this.previewImage) {
        this.previewImage.style.display = 'none';
      }
      if (this.playButton) {
        this.playButton.style.display = 'none';
      }
      if (this.video) {
        this.video.style.display = 'block';
      }
    }

    updateButtonVisibility() {
      if (this.isPlaying) {
        // Hide pause button only if not hovered - it will show on hover/touch
        if (this.pauseButton) {
          if (this.isHovered) {
            this.pauseButton.style.display = 'flex';
            this.pauseButton.style.opacity = '0.5';
          } else {
            this.pauseButton.style.display = 'none';
          }
        }
        if (this.playButton) {
          this.playButton.style.display = 'none';
        }
      } else {
        // Video is paused - show play button only if hovered and video is visible
        if (this.pauseButton) {
          this.pauseButton.style.display = 'none';
        }
        if (this.video && this.video.style.display !== 'none') {
          if (this.playButton) {
            if (this.isHovered) {
              this.playButton.style.display = 'flex';
              this.playButton.style.opacity = '0.5';
            } else {
              this.playButton.style.display = 'none';
            }
          }
        }
      }
    }

    showPauseButton() {
      if (this.pauseButton) {
        this.pauseButton.style.display = 'flex';
        this.pauseButton.style.opacity = '0.5';
      }
    }

    hidePauseButton() {
      if (this.pauseButton) {
        this.pauseButton.style.display = 'none';
      }
    }

    showPlayButton() {
      if (this.playButton) {
        this.playButton.style.display = 'flex';
        this.playButton.style.opacity = '0.5';
      }
    }

    hidePlayButton() {
      if (this.playButton) {
        this.playButton.style.display = 'none';
      }
    }

  }
  customElements.define('media-with-text-video', MediaWithTextVideo);
}

